'use client'

import { sendGenericEvent } from 'analytics/events/generics/sendGenericEvent'
import type { Product, ProductSize } from 'product/types'
import { GenericEvents } from 'product/types/GenericEvents'
import type { ProductLocations } from 'product/types/ProductLocations'
import type { Stock, StockAvailability } from 'product/types/Stock'
import {
	type DelayedSize,
	useProductSelectedColorSizes,
} from 'product-components/components/SizeSelector/hooks/useProductSelectedColorSizes/useProductSelectedColorSizes'
import { useEffect } from 'react'
import { Size } from './Size/Size'
import styles from './SizesList.module.css'

export interface DelayedTime {
	zone: string
	deliveryTimeType: 'HOURS' | 'DAYS' | 'SAMEDAY'
	minimumTime: number
	maximumTime: number
	hoursTime: number
}

export interface SelectedSize {
	size: ProductSize & StockAvailability & DelayedSize
	color: string
}

export type SizeListProps = {
	product: Product
	productId: string
	selectedColorId: string
	location: ProductLocations
	'data-testid'?: string
	onSelectedSize: (selectedSize: SelectedSize) => void
}

export interface SizeListExtended extends SizeListProps {
	delayedMessage?: string
	productStock?: Stock
	delayedWareHouse?: (warehousesIds?: string[]) => boolean
	location: ProductLocations
}

export function SizesList({
	product,
	productId,
	selectedColorId,
	onSelectedSize,
	...extended
}: Readonly<SizeListProps | SizeListExtended>) {
	const { delayedMessage, delayedWareHouse, productStock, location } =
		extended as SizeListExtended

	const { sizes, selectedProductId } = useProductSelectedColorSizes({
		product,
		productId,
		selectedColorId,
		delayedWareHouse,
		preComputedStock: productStock,
	})

	useEffect(() => {
		sendGenericEvent(GenericEvents.SHOW_SIZE_SELECTOR)
	}, [])

	return (
		<ol
			className={styles.list}
			data-testid={'product.sizesBottomSheet.sizesList'}
		>
			{sizes.map((size) => (
				<li key={size.id} className={styles.item}>
					<Size
						size={size}
						location={location}
						selectedColorId={selectedColorId}
						selectedProductId={selectedProductId}
						delayedMessage={delayedMessage}
						onSelectedSize={onSelectedSize}
					/>
				</li>
			))}
		</ol>
	)
}
